import { useEffect, useMemo, useState } from "react";
import { Plus, Search, X } from "lucide-react";
import { api } from "../../lib/api";
import { BR, fmtL, sel, btnS } from "./BpKit";

let seq = 0;
export const newKey = () => `k${Date.now()}${seq++}`;

export const AddAccount = ({ suggestions, rows, branches, onAdd, disabled }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [remote, setRemote] = useState([]);
  const taken = useMemo(() => new Set(rows.map((r) => `${r.account_id}:${r.branch}`)), [rows]);
  useEffect(() => {
    if (q.trim().length < 2) { setRemote([]); return undefined; }
    const t = setTimeout(() => api.get("/accounts", { params: { q: q.trim(), page_size: 15 } }).then((r) => setRemote(r.data.items)).catch(() => setRemote([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  const ql = q.trim().toLowerCase();
  const fix = (s) => ({ ...s, branch: branches.includes(s.branch) ? s.branch : branches[0] });
  const fresh = (list) => {
    const seen = new Set(taken);
    return list.map(fix).filter((s) => { const k = `${s.account_id}:${s.branch}`; if (seen.has(k)) return false; seen.add(k); return true; });
  };
  const mine = fresh(suggestions.filter((s) => !ql || s.account_name.toLowerCase().includes(ql))).slice(0, 12);
  const ids = new Set(mine.map((s) => s.account_id));
  const others = fresh(remote.filter((a) => !ids.has(a.account_id)).map((a) => ({ account_id: a.account_id, account_name: a.name, branch: a.branch, segment: a.biz_segment || "new", ly: null }))).slice(0, 8);
  const pick = (s) => { onAdd(s); setQ(""); setOpen(false); };
  const ly = fresh(suggestions.filter((s) => s.ly > 0));
  return (
    <div className="flex flex-wrap items-start gap-2">
      <div className="relative w-full sm:w-96">
        <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
        <input data-testid="bp-my-plan-add-account-search" disabled={disabled} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          placeholder="Add an account: type a name" className={`${sel} w-full pl-8`} />
        {open && (mine.length > 0 || others.length > 0) && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-300 rounded-sm shadow-lg max-h-72 overflow-auto" data-testid="bp-add-account-options">
            {mine.length > 0 && <div className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Your accounts</div>}
            {[...mine, ...others].map((s, i) => (
              <button key={`${s.account_id}-${s.branch}`} type="button" data-testid={`bp-add-account-option-${i}`} onClick={() => pick(s)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex justify-between gap-2">
                <span className="truncate">{s.account_name} <span className="text-xs text-slate-500">· {BR[s.branch]}</span></span>
                {s.ly > 0 && <span className="num text-xs text-slate-500 shrink-0">LY {fmtL(s.ly)}</span>}
              </button>
            ))}
            <button type="button" className="w-full text-right px-3 py-1.5 text-xs text-slate-500 border-t" onClick={() => setOpen(false)} data-testid="bp-add-account-close"><X className="h-3 w-3 inline" /> Close</button>
          </div>
        )}
      </div>
      <button type="button" data-testid="bp-my-plan-add-account-button" className={btnS} disabled={disabled || !ly.length} onClick={() => ly.forEach(onAdd)}
        title="Adds every account that booked POs with you last year">
        <Plus className="h-4 w-4 inline mr-1" />Add last year's accounts ({ly.length})
      </button>
    </div>
  );
};

export const LinkAccount = ({ r, onLink, locked }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(r.account_name);
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (!open || q.trim().length < 2) { setRes([]); return undefined; }
    const t = setTimeout(() => api.get("/accounts", { params: { q: q.trim(), page_size: 6 } }).then((x) => setRes(x.data.items)).catch(() => setRes([])), 250);
    return () => clearTimeout(t);
  }, [q, open]);
  if (locked) return <span className="text-amber-700" data-testid={`bp-row-unlinked-${r.key}`}>Not linked to an account</span>;
  if (!open) return <button type="button" data-testid={`bp-row-link-${r.key}`} onClick={() => setOpen(true)} className="text-amber-700 hover:underline">Not linked · link account</button>;
  return (
    <div className="mt-1 space-y-1 max-w-[240px]">
      <div className="flex gap-1">
        <input data-testid={`bp-row-link-search-${r.key}`} autoFocus value={q} onChange={(e) => setQ(e.target.value)} className={`${sel} h-7 text-xs flex-1 min-w-0`} />
        <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid={`bp-row-link-close-${r.key}`}><X className="h-3.5 w-3.5" /></button>
      </div>
      {res.map((a, i) => (
        <button key={a.account_id} type="button" data-testid={`bp-row-link-option-${r.key}-${i}`} onClick={() => { onLink(r.key, a); setOpen(false); }}
          className="block w-full text-left text-xs px-2 py-1 border border-slate-200 rounded-sm hover:border-primary hover:bg-orange-50/40 truncate">{a.name} · {BR[a.branch] || a.branch}</button>
      ))}
    </div>
  );
};

export const RowLead = ({ r, branches, locked, onField, ly }) => {
  const s = `${sel} h-8 text-xs px-1`;
  return (
    <>
      <td><select data-testid={`bp-row-branch-${r.key}`} disabled={locked} className={s} value={r.branch} onChange={(e) => onField(r.key, "branch", e.target.value)}>
        {branches.map((b) => <option key={b} value={b}>{BR[b]}</option>)}</select></td>
      <td><select data-testid={`bp-row-category-${r.key}`} disabled={locked} className={s} value={r.category} onChange={(e) => onField(r.key, "category", e.target.value)}>
        {["A", "B", "C"].map((c) => <option key={c} value={c}>{c}</option>)}</select></td>
      <td><select data-testid={`bp-row-segment-${r.key}`} disabled={locked} className={s} value={r.segment} onChange={(e) => onField(r.key, "segment", e.target.value)}>
        <option value="new">New</option><option value="repeat">Repeat</option></select></td>
      <td className="text-right num text-xs text-slate-500 whitespace-nowrap">{ly == null ? "—" : fmtL(ly)}</td>
    </>
  );
};
